import { HasVariableScope } from './has-variable-scope.js'
import { RestrictableItem } from './restrictions/restrictable-item.js'
import { TypeReference } from '../data/type-reference.js'
import { JsonMappable } from '../data/json-mappable.js'
import type {
	UserIdType,
	ModuleIdType,
	BusinessObjectTypeType,
	BusinessObjectIdType,
	TsCodeType,
	TsTypeType,
	NameAndType,
} from '../types.js'
import {
	jsonClass,
	jsonProperty,
	getJsonClassName,
} from '@sumbricht/json-class-serializer'
import { CanBeSecret } from '../types.js'
import { BoReference } from './bo-reference.js'
import { BoVisitor } from './bo-visitor.js'
import { BusinessObjectWithDataFile } from './business-object-with-data-file.js'
import {
	BoSpecificDetails,
	BoSpecificDetailsQuery,
} from './bo-specific-details.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

type BoLike = { moduleId: ModuleIdType; boId: BusinessObjectIdType }
export function boSort(a: BoLike, b: BoLike) {
	const moduleIdSort = a.moduleId.localeCompare(b.moduleId)
	if (moduleIdSort != 0) return moduleIdSort
	if ('boId' in a && 'boId' in b) {
		return a.boId.localeCompare(b.boId)
	}
	throw Error('Cannot sort BoLike objects without id or boId')
}

export interface ValidateAndFixResult {}
export interface BoRepositoryInspector {
	getBoSpecificDetails(
		boReference: BoReference,
		alsoSearchInIncludedModules: boolean,
		query: BoSpecificDetailsQuery,
	): BoSpecificDetails
}

@jsonClass('BusinessObject')
export class BusinessObject
	implements JsonMappable, CanBeSecret, HasVariableScope
{
	@jsonProperty(String)
	get __type(): BusinessObjectTypeType {
		return getJsonClassName(this) as BusinessObjectTypeType
	}
	set __type(_) {
		/*ignore*/
	}

	@jsonProperty(String) boId: BusinessObjectIdType = ''
	@jsonProperty(String) moduleId: ModuleIdType = ''
	// @jsonProperty(String) type: BusinessObjectTypYeType = ''
	@jsonProperty(Date) creationTimestamp: Date = new Date()
	@jsonProperty(Date) updateTimestamp: Date
	@jsonProperty(String) lastModifiedBy?: UserIdType

	@jsonProperty(Boolean) isModifiable: boolean = true
	@jsonProperty(Boolean) isDeletable: boolean = true
	@jsonProperty(Boolean) isSecret: boolean = false

	@jsonProperty(String) systemVersion!: string
	@jsonProperty(Boolean) isDeleted?: boolean

	readonly affectsDb: boolean = false

	constructor(init?: Partial<BusinessObject>) {
		this.creationTimestamp = this.updateTimestamp = new Date()
		assignCommonProperties(this, init)
	}

	initializeNewBo(repositoryInspector: BoRepositoryInspector) {}

	getQualifiedName() {
		return `${this.moduleId}.${this.boId}`
	}

	getQualifiedTypeAndName() {
		return `${this.__type}:${this.moduleId}.${this.boId}`
	}

	getImportName() {
		return `${this.moduleId}__${this.boId}`
	}

	getDependencies(allBos: BusinessObject[]): BusinessObject[] {
		return []
	}

	getTypeReferences(): TypeReference[] {
		return []
	}

	getClassNames(): string[] {
		return []
	}

	getRestrictableItems(): RestrictableItem[] {
		return []
	}

	getRestrictableItem(itemName: string): RestrictableItem | undefined {
		return this.getRestrictableItems().find((i) => i.itemName == itemName)
	}

	validateAndFix(): ValidateAndFixResult {
		return {}
	}

	getBoReference(includeBoType = true) {
		return new BoReference({
			moduleId: this.moduleId,
			boId: this.boId,
			boType: includeBoType ? this.__type : undefined!,
		})
	}

	getBoSpecificDetails(query?: BoSpecificDetailsQuery): BoSpecificDetails {
		const restrictableItems = this.getRestrictableItems()
		return {
			restrictableItems: restrictableItems.length
				? restrictableItems
				: undefined,
		}
	}

	hasDataFile(): this is BusinessObjectWithDataFile {
		return false
	}

	visit(visitor: BoVisitor) {}

	getVariableScope(
		includeDeclarationVariables: boolean,
	): Record<string, string> {
		return {}
	}

	getPropertiesForPropertyTree(): NameAndType[] {
		return []
	}
}
